import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Building2, Radar as RadarIcon, Layers } from 'lucide-react';
import type { MarketPulse } from '../../services/watchApi';

/** Value pills with live pool counts — shared by Watch feed and AI Job-Radar. */
const LiveRadarStrip: React.FC<{
  pulse: MarketPulse | null;
  /** @deprecated no longer rendered as a pill; kept for caller compatibility. */
  unreadTotal?: number;
  className?: string;
  /** Pulsing live dot left of the pills (no label). */
  showPulseDot?: boolean;
  /** Full "Vee is scanning" line above pills (How-it-works modal). */
  showScanningLabel?: boolean;
  /** Render the per-stat icons (off for a cleaner single text line). */
  showIcons?: boolean;
}> = ({
  pulse,
  className = '',
  showPulseDot = true,
  showScanningLabel = false,
  showIcons = true,
}) => {
  const { t } = useTranslation('watch');
  if (!pulse) return null;

  const stats = [
    {
      key: 'direct',
      icon: Building2,
      value: t('pills.directShort', { count: pulse.companies_hiring }),
      full: t('pills.direct'),
    },
    {
      key: 'jobs',
      icon: RadarIcon,
      value: t('pills.sourcesShort', { count: pulse.jobs_active }),
      full: t('pills.sources'),
    },
    {
      key: 'sources',
      icon: Layers,
      value: t('pills.sourcesLabel'),
      full: t('pills.sources'),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={`mt-2.5 ${className}`}
    >
      {showScanningLabel && (
        <div className="flex items-center gap-1.5 mb-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-pink-500" />
          </span>
          <span className="text-[11px] font-semibold text-slate-500 tracking-wide">
            {t('pulse.scanning')}
          </span>
        </div>
      )}
      {/* One thin, number-forward stat line — compact on mobile. */}
      <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5 min-w-0 text-[11px] sm:text-xs leading-tight">
        {showPulseDot && !showScanningLabel && (
          <>
            <span className="relative flex h-1.5 w-1.5 flex-shrink-0" aria-hidden>
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-pink-500" />
            </span>
            <span className="text-[10px] font-semibold text-pink-500 uppercase tracking-wide mr-1">
              {t('pulse.liveLabel')}
            </span>
          </>
        )}
        {stats.map(({ key, icon: Icon, value, full }, i) => (
          <React.Fragment key={key}>
            {i > 0 && <span className="text-slate-300" aria-hidden>·</span>}
            <span className="inline-flex items-center gap-1 text-slate-500 whitespace-nowrap" title={full}>
              {showIcons && <Icon className="w-3 h-3 text-pink-500 flex-shrink-0" />}
              <span className="font-semibold text-slate-700">{value}</span>
            </span>
          </React.Fragment>
        ))}
      </div>
    </motion.div>
  );
};

export default LiveRadarStrip;
